import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CuideiStep } from 'constants/cuidei'
import { createCuideiConsultation } from 'redux/thunks/create-cuidei-consultation'
import { Consultation } from 'types'
import {
  CuideiAttribution,
  CuideiGeneralAnswers,
  CuideiPathology,
  CuideiPathologyAnswers,
  CuideiPatientForm
} from 'types/cuidei'
import { mergeConsultationWithoutLiveRegression } from 'utils/consultationRealtime'

export interface CuideiState {
  /** Pharmacy attribution captured at landing, persisted through the flow. */
  attribution: CuideiAttribution | null
  /** Current step of the patient flow. */
  step: CuideiStep
  /** Missing on progress persisted before questionnaire-first onboarding. */
  pathologyFlowVersion?: number
  /** Step 1 form data. */
  patientForm: Partial<CuideiPatientForm>
  /** The live (waiting-line) consultation created after the form. */
  consultation: Consultation | null
  /** Whether the patient accepted the combined document-delivery consent. */
  documentDeliveryAuthorized: boolean
  /** Post-consultation feedback (1–5) once submitted. */
  feedbackRating: number | null
  isCreatingConsultation: boolean
  createError: string | null
  /** Pathology landing the patient came from (null = pharmacy /farma flow). */
  pathology: CuideiPathology | null
  /** Answers to the pathology questions, keyed by question id. */
  pathologyAnswers: CuideiPathologyAnswers
  /** Fixed general block (height/weight). */
  generalAnswers: CuideiGeneralAnswers
}

/** Steps after payment — the consultation is paid and must never be reset by a flow switch. */
const PAID_FLOW_STEPS: CuideiStep[] = [
  CuideiStep.Finalizar,
  CuideiStep.Conta,
  CuideiStep.FilaVideo,
  CuideiStep.Consentimento,
  CuideiStep.Feedback
]

const PATHOLOGY_FLOW_VERSION = 2

/** Pathology flows open on the questionnaire; /farma on the patient data. */
const firstStep = (pathology: CuideiPathology | null): CuideiStep =>
  pathology ? CuideiStep.Questionario : CuideiStep.Dados

const initialState: CuideiState = {
  attribution: null,
  step: CuideiStep.Dados,
  pathologyFlowVersion: PATHOLOGY_FLOW_VERSION,
  patientForm: {},
  consultation: null,
  documentDeliveryAuthorized: false,
  feedbackRating: null,
  isCreatingConsultation: false,
  createError: null,
  pathology: null,
  pathologyAnswers: {},
  generalAnswers: { heightCm: '', weightKg: '' }
}

export const cuideiSlice = createSlice({
  name: 'cuidei',
  initialState,
  reducers: {
    setCuideiAttribution: (state, { payload }: PayloadAction<CuideiAttribution>) => {
      // First real pharmacy attribution wins. A generic Cuidei visit persists an
      // attribution object with pharmacyId=null; a later QR scan must be allowed
      // to upgrade it, otherwise the consultation has no pharmacy and delivery
      // silently has nowhere to go.
      if (!state.attribution || (!state.attribution.pharmacyId && payload.pharmacyId)) {
        state.attribution = payload
      }
    },
    setCuideiStep: (state, { payload }: PayloadAction<CuideiStep>) => {
      state.step = payload
    },
    updateCuideiPatientForm: (state, { payload }: PayloadAction<Partial<CuideiPatientForm>>) => {
      state.patientForm = { ...state.patientForm, ...payload }
    },
    setCuideiConsultation: (state, { payload }: PayloadAction<Consultation | null>) => {
      state.consultation = payload
        ? mergeConsultationWithoutLiveRegression(state.consultation ?? undefined, payload)
        : null
    },
    setCuideiDocumentDeliveryAuthorized: (state, { payload }: PayloadAction<boolean>) => {
      state.documentDeliveryAuthorized = payload
    },
    setCuideiFeedbackRating: (state, { payload }: PayloadAction<number | null>) => {
      state.feedbackRating = payload
    },
    /**
     * Called by every Cuidei flow page on mount with its pathology (null on
     * /farma). Switching pathology — or between a pathology and /farma —
     * restarts the flow so answers never leak into another consultation. The
     * same pathology (a reload) keeps progress; a new questions version only
     * drops the now-stale answers.
     */
    enterCuideiFlow: (state, { payload }: PayloadAction<CuideiPathology | null>) => {
      const previous = state.pathology
      if ((previous?.slug ?? null) !== (payload?.slug ?? null)) {
        // A paid consultation (account/queue/consent/feedback) must survive the
        // switch: the patient area sends pathology patients to /farma/consulta
        // for the post-video consent. Only the answers are dropped.
        // It stays attributed to its own pathology (tracking, progress bar).
        if (state.consultation && PAID_FLOW_STEPS.includes(state.step)) {
          state.pathologyAnswers = {}
          state.generalAnswers = initialState.generalAnswers
          return
        }
        const { attribution } = state
        Object.assign(state, initialState, { attribution, pathology: payload, step: firstStep(payload) })
        return
      }
      // Flow-schema migrations must not depend on an admin questions-version
      // change: old Dados used to precede the questionnaire even at the same version.
      if (payload && state.pathologyFlowVersion !== PATHOLOGY_FLOW_VERSION) {
        if (state.step !== CuideiStep.Pagamento && !PAID_FLOW_STEPS.includes(state.step)) {
          state.step = CuideiStep.Questionario
        }
        state.pathologyFlowVersion = PATHOLOGY_FLOW_VERSION
      }
      // Unpaid pathology progress from before the questionnaire came first
      // (old "perguntas"/"bloco_geral" steps) restarts at the questionnaire.
      if (payload && !Object.values(CuideiStep).includes(state.step)) state.step = CuideiStep.Questionario
      if (previous && payload && previous.version !== payload.version) {
        state.pathologyAnswers = {}
        if (state.step === CuideiStep.Dados) state.step = CuideiStep.Questionario
      }
      state.pathology = payload
    },
    setCuideiPathologyAnswers: (state, { payload }: PayloadAction<CuideiPathologyAnswers>) => {
      state.pathologyAnswers = payload
    },
    setCuideiGeneralAnswers: (state, { payload }: PayloadAction<CuideiGeneralAnswers>) => {
      state.generalAnswers = payload
    },
    /** Reset the flow but keep the attribution + pathology (a retry stays on the same landing). */
    resetCuideiFlow: (state) => {
      const { attribution, pathology } = state
      Object.assign(state, initialState, { attribution, pathology, step: firstStep(pathology) })
    },
    resetCuideiState: () => initialState
  },
  extraReducers: (builder) =>
    builder
      .addCase(createCuideiConsultation.pending, (state) => {
        state.isCreatingConsultation = true
        state.createError = null
      })
      .addCase(createCuideiConsultation.fulfilled, (state, { payload }) => {
        state.isCreatingConsultation = false
        state.consultation = payload
      })
      .addCase(createCuideiConsultation.rejected, (state, { error }) => {
        state.isCreatingConsultation = false
        state.createError = error.message ?? 'Falha ao criar a consulta.'
      })
})

export const {
  setCuideiAttribution,
  setCuideiStep,
  updateCuideiPatientForm,
  setCuideiConsultation,
  setCuideiDocumentDeliveryAuthorized,
  setCuideiFeedbackRating,
  resetCuideiFlow,
  resetCuideiState,
  enterCuideiFlow,
  setCuideiPathologyAnswers,
  setCuideiGeneralAnswers
} = cuideiSlice.actions

export default cuideiSlice.reducer
